/**
 * Color utility functions for dynamic theme generation.
 * Derives a full MUI palette from primary + secondary hex colors.
 */

type RGB = { r: number; g: number; b: number };
type HSL = { h: number; s: number; l: number };

function hexToRgb(hex: string): RGB {
	const clean = hex.replace("#", "");
	return {
		r: parseInt(clean.substring(0, 2), 16),
		g: parseInt(clean.substring(2, 4), 16),
		b: parseInt(clean.substring(4, 6), 16),
	};
}

function rgbToHex({ r, g, b }: RGB): string {
	const toHex = (n: number) =>
		Math.max(0, Math.min(255, Math.round(n)))
			.toString(16)
			.padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl({ r, g, b }: RGB): HSL {
	const rN = r / 255;
	const gN = g / 255;
	const bN = b / 255;
	const max = Math.max(rN, gN, bN);
	const min = Math.min(rN, gN, bN);
	const l = (max + min) / 2;
	let h = 0;
	let s = 0;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case rN:
				h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6;
				break;
			case gN:
				h = ((bN - rN) / d + 2) / 6;
				break;
			case bN:
				h = ((rN - gN) / d + 4) / 6;
				break;
		}
	}

	return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb({ h, s, l }: HSL): RGB {
	const sN = s / 100;
	const lN = l / 100;
	const hN = h / 360;

	if (sN === 0) {
		const v = Math.round(lN * 255);
		return { r: v, g: v, b: v };
	}

	const hue2rgb = (p: number, q: number, t: number) => {
		let tN = t;
		if (tN < 0) tN += 1;
		if (tN > 1) tN -= 1;
		if (tN < 1 / 6) return p + (q - p) * 6 * tN;
		if (tN < 1 / 2) return q;
		if (tN < 2 / 3) return p + (q - p) * (2 / 3 - tN) * 6;
		return p;
	};

	const q = lN < 0.5 ? lN * (1 + sN) : lN + sN - lN * sN;
	const p = 2 * lN - q;

	return {
		r: Math.round(hue2rgb(p, q, hN + 1 / 3) * 255),
		g: Math.round(hue2rgb(p, q, hN) * 255),
		b: Math.round(hue2rgb(p, q, hN - 1 / 3) * 255),
	};
}

export function lighten(hex: string, amount: number): string {
	const hsl = rgbToHsl(hexToRgb(hex));
	hsl.l = Math.min(100, hsl.l + amount);
	return rgbToHex(hslToRgb(hsl));
}

export function darken(hex: string, amount: number): string {
	const hsl = rgbToHsl(hexToRgb(hex));
	hsl.l = Math.max(0, hsl.l - amount);
	return rgbToHex(hslToRgb(hsl));
}

/**
 * Returns relative luminance of a color (0-1).
 * Used for WCAG contrast calculations.
 */
function getLuminance(hex: string): number {
	const { r, g, b } = hexToRgb(hex);
	const [rL, gL, bL] = [r, g, b].map((c) => {
		const s = c / 255;
		return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
	});
	return 0.2126 * rL + 0.7152 * gL + 0.0722 * bL;
}

/**
 * Returns black or white text color for best contrast (WCAG AA).
 */
export function getContrastText(hex: string): string {
	return getLuminance(hex) > 0.179 ? "#000000" : "#FFFFFF";
}

/**
 * WHY THESE DEFAULTS HOLD JANUS VALUES, AND WHY THAT IS NOT A BUG.
 *
 * `colors_and_type.css` defines `--surface-tint`, `--surface-2`, `--divider` and
 * `--brand-tertiary-main` once per product (sims, nalcam, janus, parca). The constants below
 * carry the JANUS values, which looks like the wrong block was read — and it is worth
 * recording why it is not, because the mistake is easy to make twice.
 *
 * This bundle is MULTI-ACCOUNT. No runtime code branches on product, but that is because the
 * differentiation is by DATA: each account's `themeColors` supplies its own palette, and the
 * logo comes from the per-account `brandInformation.logo` rather than from the bundle. So
 * "no tenant branching in src/" does not mean "SIMS only" — it means the product identity
 * lives in settings, which is the design.
 *
 * JANUS is one of those accounts and is served by this bundle. SIMS-8458 was specified
 * against the JANUS design block for that reason (see
 * `__tests__/utils/janusBrandColours.test.ts`), and
 * `__tests__/utils/clinicalStatusColors.guard.test.ts` captured its baseline "against the
 * B2D/JANUS tenant config". Repointing these to the SIMS block would therefore change what
 * JANUS renders — the defaults are load-bearing for whichever account has not overridden
 * them, and SettingsModal drops a value that equals the default, so an account that
 * "chose" the default relies on it.
 *
 * WHAT THIS MEANS IN PRACTICE. An account whose design differs from these defaults must set
 * the role in its own settings, not have the default moved. For SIMS that is `surfaceTint`:
 * the hand-off gives `--surface-tint: #E9D0F4` and the default is JANUS's `#DFF5FF`, which
 * is why the Actions table header and active filter chip render pale blue. The field is
 * "Surface Tint" on the settings screen and it saves correctly, precisely because #E9D0F4
 * differs from the default. Recorded in
 * `aidlc-docs/SIMS-8592/construction/sims-tenant-theme-backfill.md`.
 *
 * The genuinely shared roles — `DEFAULT_SUCCESS_*`, `DEFAULT_WARNING_*`, `DEFAULT_TEXT_*`,
 * the ink pair and the surface pair — come from the base `:root` block, outside every
 * product override, and are the same for all four.
 */

/**
 * Documented default for the `tertiary` palette role — the SIMS design's
 * `--brand-tertiary-main`. Used when the account provides no `tertiaryColor`
 * (SIMS-7505 AC2) — a distinct constant, NOT derived from primary/secondary.
 */
export const DEFAULT_TERTIARY_COLOR = "#7034E7";

/**
 * Documented global default for the `surfaceTint` palette role (JANUS
 * `--surface-tint` / `--janus-blue-100`). Used when a tenant provides no
 * `surfaceTint` (SIMS-7608 AC2) — a distinct role, NOT `surfaceLight`/`#FFFFFF`
 * or a derived value. Per-tenant values (SIMS #E9D0F4, …) are backfilled in SIMS-7513.
 */
export const DEFAULT_SURFACE_TINT = "#DFF5FF";

/**
 * Documented global defaults for the `success` pair (SIMS-8458 AC1).
 *
 * The design's `--success-light` / `--success-dark`. Until this landed, `generatePalette`
 * emitted no `success` key at all, so `createTheme` merged MUI's stock green
 * (`#4caf50` / `#1b5e20`) — which is why the Active status pill, the "ok" journey-step
 * icon and the Incentive History amount all rendered green rather than JANUS lime. Those
 * three consumers already read `palette.success.*`, so defining the role is the whole fix;
 * none of them needed a component change.
 *
 * A DISTINCT role, deliberately not `outlineDark` (SIMS-8458 notes): for the JANUS tenant
 * `outlineDark` happens to hold `#AEDD49` too, but it also drives ~38 borders AND the
 * Excused-absence / Missed-visit clinical status colours (`general.ts:30/34`). Aliasing the
 * two would silently recolour those clinical states — AC5 forbids it, and
 * `__tests__/utils/clinicalStatusColors.guard.test.ts` fails if anyone tries.
 *
 * Accessibility, flagged on the ticket and unresolved: `#408103` on `#AEDD49` is 3.04:1 —
 * below WCAG AA 4.5 for normal text, though it clears the 3:1 bar for large/bold text and
 * UI components. The Active pill's Chip label is `size="small"`, i.e. normal text.
 */
export const DEFAULT_SUCCESS_LIGHT = "#AEDD49";
export const DEFAULT_SUCCESS_DARK = "#408103";

/**
 * Documented global default for the `brandFaint` role (SIMS-8458 AC2) — the design's
 * `--brand-primary-faint`, used by the selected day card in Daily Journey.
 *
 * A DISTINCT role, deliberately NOT a re-derived `primary.light` (SIMS-8458 notes):
 * `primary.light` is consumed by `MenuItemIcon` / `MenuItemButton` for inactive sidebar
 * text, so deriving this from it would tie the selected-day treatment to the menu's
 * appearance. AC2 also requires it be "visibly distinct from the sidebar/menu colour".
 */
export const DEFAULT_BRAND_FAINT = "#5AAB96";

/**
 * Documented global default for the `surfaceTwo` role (SIMS-8458 AC11) — the design's
 * `--surface-2`.
 *
 * A DISTINCT role rather than a derived one. The Daily Journey "not started yet" step
 * icon previously used `surface.dark`, which is `darken(surfaceColor, 8)`: it drifts with
 * whatever surface a tenant configures and renders a teal-tinted grey (#c6d6d6 for JANUS)
 * where the design wants a cool grey. SIMS-7532 AC1 names four step-icon colours —
 * grey / orange / green / red — and this is the grey.
 *
 * Same value as `divider` on purpose: the design defines `--divider: var(--surface-2)`,
 * so the framed-table border and this surface are one token. They are kept as separate
 * roles because they are separate *concepts* — a tenant could reasonably retint one
 * without the other — but they share a default.
 */
export const DEFAULT_SURFACE_TWO = "#D2DCDF";

/**
 * Documented global defaults for the `warning` pair (SIMS-8458 AC10) — the design's
 * `--warning-light` / `--warning-dark`.
 *
 * The light half was `#FFE7E0`, a pale PINK, where the design specifies `#FFE7C2`, a warm
 * amber. Both the Daily Journey step icons and the day-detail status pills read this pair
 * (the authoring source uses `var(--warning-light)` for both), so "Pending" and "Overdue"
 * rendered pinkish rather than the orange SIMS-7532 AC1 calls for. The dark half was
 * already correct.
 *
 * Unlike the roles above, `warning` stays FIXED — not exposed in the colour settings
 * screen. It is a semantic role: orange means "awaiting action" regardless of tenant
 * brand. (`success` is configurable via AC7 only because AC1 had to define a role that
 * did not exist; that asymmetry is acknowledged on the ticket and left to a separate
 * theming-policy decision rather than settled here.)
 */
export const DEFAULT_WARNING_LIGHT = "#FFE7C2";
export const DEFAULT_WARNING_DARK = "#F26716";

/**
 * The design's `--success-ink` / `--warning-ink` — text colours for a 12px pill sitting on
 * the matching `-light` fill (SIMS-8592 styles pass).
 *
 * WHY THESE EXIST AS THEIR OWN ROLES. `handoff.md` states it directly: "Status pills are
 * 12px -> ink tokens only; the fill-only `-dark` values fail contrast at that size." The
 * numbers on the SIMS palette:
 *
 *   success  #408103 on #AEDD49 = 3.04:1   ink #2C5A02 on #AEDD49 = 5.15:1
 *   warning  #F26716 on #FFE7C2 = 2.60:1   ink #7A3200 on #FFE7C2 = 7.65:1
 *
 * WCAG AA wants 4.5:1 for normal text, and a 12px Chip label is normal text. The warning
 * pair at 2.60:1 misses even the 3:1 large-text/UI floor, so the `-dark` values are not a
 * near miss — they are unusable as pill text. `__tests__/pages/Actions/typeChipStyles.test.ts`
 * pins every shipped pair above 4.5:1 so a later token change cannot quietly drop one.
 *
 * NOT a claim that the screen was previously inaccessible. Before this, the chips used MUI's
 * filled intents — `main` + `contrastText` — which contrast fine; they simply did not look
 * like the design. The failing combination is `light` + `dark`, which is what the design
 * system's own `.c-pill--success` specifies, so it is the obvious route to the design's
 * appearance and the one these roles exist to avoid taking.
 *
 * WHERE THEY COME FROM. `colors_and_type.css` does NOT define them, despite handoff.md
 * listing them as "per DS" — they are declared in the prototype's own inline style block
 * (`Actions Pattern v2.dc.html:21`, `[data-theme] { --warning-ink: #7A3200;
 * --success-ink: #2C5A02; }`). Raised with the design owner: the design system's
 * `.c-pill--success` still resolves to `--success-dark`, so the DS class and handoff.md
 * disagree and the prototype follows the DS. Implemented per handoff.md, which is the
 * screen-specific document and the one that gives a reason.
 *
 * FIXED, not tenant-configurable — the same policy as the `warning` pair above. An ink is
 * the readable partner of a specific fill, not a brand choice. The known gap: `successLight`
 * IS configurable (SIMS-8458 AC7), so a tenant that overrides it away from #AEDD49 can
 * push this pair below AA. That asymmetry is the theming-policy question the `warning`
 * docblock above already defers, and it is recorded here rather than half-solved.
 */
export const DEFAULT_SUCCESS_INK = "#2C5A02";
export const DEFAULT_WARNING_INK = "#7A3200";

/**
 * Documented global defaults for the `surface` pair — the design's `--surface-1`, and the
 * `--brand-primary-wash` the surface's dark step resolves to (SIMS-8592 styles pass).
 *
 * THESE EXIST BECAUSE THE TWO ENDS DISAGREED ABOUT "ABSENT". `SettingsModal` omits a role
 * from `themeColors` when it equals the documented default, so defaults stay implicit and a
 * later change to one still reaches every tenant. For surface it omits on `#F7F4F9`
 * (`SettingsModal/index.tsx`, the `state.surface !== "#F7F4F9"` guard) and on `#E9D0F4` for
 * the dark step — but `generatePalette` had no constant to fall back TO, so it derived
 * `lighten(primaryColor, 42)` instead.
 *
 * The consequence was not subtle, and it is why these are constants now rather than a note:
 * the one surface value the settings screen COULD NOT SAVE was the design's own. Typing
 * `#F7F4F9` dropped it, and the screen came back `lighten("#52197B", 42)` = `#BD84E6`, a
 * mid-lavender page. Observed on the SIMS account on 2026-09-17.
 *
 * Lightening a brand colour cannot reach a near-white in any case: `#52197B` is lightness 29
 * at saturation 66, so +42 lands at l=71 with the saturation untouched, where `--surface-1`
 * is l=97/s=29. So the old fallback was wrong for every tenant with a dark, saturated
 * primary — not only for SIMS.
 *
 * `__tests__/utils/settingsDefaultsAgree.test.ts` pins the agreement in both directions, so
 * a default changed at one end and not the other fails there instead of silently resurfacing.
 */
export const DEFAULT_SURFACE_COLOR = "#F7F4F9";
export const DEFAULT_SURFACE_DARK = "#E9D0F4";

/**
 * Documented global default for MUI's `divider` role (SIMS-8458 AC4) — the design's
 * `--divider`.
 *
 * `generatePalette` never emitted a `divider`, so MUI merged its own
 * `rgba(0, 0, 0, 0.12)`. Being translucent, that composites differently against whatever
 * sits behind it — `#E0E0E0` on white, `#D7DADB` on a `--surface-1` zebra row — so the
 * same border read as two different greys within one table. A solid value is the same
 * everywhere, which is the real win here; the colour shift itself is slight
 * (luminance 0.697 → 0.702 over `--surface-1`).
 *
 * ~42 call sites read it, roughly 28 of them outside the screens this initiative
 * restyled — app-wide is intended for a foundation ticket. AC5's "existing borders are
 * unchanged" refers to the `outline.dark`-drawn borders, which this does not touch.
 *
 * The design defines `--divider: var(--surface-2)`, and the JANUS block sets
 * `--surface-2: #D2DCDF` — so the divider and the "Completed" pill's grey surface are the
 * same token, which is why AC4 names one value for both.
 */
export const DEFAULT_DIVIDER = "#D2DCDF";

/**
 * Documented global defaults for body and secondary text (SIMS-8458 AC4) — the design's
 * `--text-primary` / `--text-secondary`, read from the authoring source's `:root` block.
 *
 * AC4 asks that these resolve "across every theme role documented as carrying that text".
 * The documenting artifact is `THEME_COLOR_MAP.md`, which resolves it to six roles:
 * body text — `textMain.dark2`, `textPrimary.main`, MUI `text.primary`; secondary/muted —
 * MUI `text.secondary`, `textMain.main2`, `textSecondary.main`. Roughly 310 call sites, and
 * afterwards the app's three parallel text families are aliases of these two values rather
 * than three competing systems.
 *
 * NOT in scope, recorded so the gap is visible rather than silently half-done: the design
 * also carries `--text-disabled` / `--text-placeholder` `#B0B6B6` (nearest role
 * `textMain.dark`, `#CCCCCE`, ~74 sites — the largest single text role in the app) and
 * `--text-link`, which the design derives from brand-primary where the app derives
 * `textLinkHelpSupport` from `secondaryColor`. Neither is body or muted text, so AC4's
 * wording does not reach them; each wants its own ticket.
 */
export const DEFAULT_TEXT_PRIMARY = "#1F2626";
export const DEFAULT_TEXT_SECONDARY = "#575C5C";

export interface ThemeColorsConfig {
	primaryColor: string;
	secondaryColor: string;
	surfaceColor?: string;
	surfaceLight?: string;
	surfaceDark?: string;
	primaryDark?: string;
	secondaryLight?: string;
	outlineMain?: string;
	outlineDark?: string;
	textMainDark2?: string;
	textMainMain2?: string;
	textSecondaryMain?: string;
	tertiaryColor?: string;
	surfaceTint?: string;
	// SIMS-8458 — design-system roles, configurable per tenant (AC7/AC8). Each falls back
	// to its documented default when the tenant has not overridden it, and SettingsModal
	// emits it into `themeColors` ONLY when it differs from that default, so defaults stay
	// implicit and a later change to a default still reaches tenants that never set one.
	successLight?: string;
	successDark?: string;
	brandFaint?: string;
	dividerColor?: string;
	surfaceTwo?: string;
	textPrimaryMain?: string;
	fontFamily?: string;
}

/**
 * Generates the full themePalette structure from theme color configuration.
 *
 * Based on the B2D brand spec:
 *  - primaryColor (#276048) -> navigation, menu, buttons, key highlights, accent
 *  - secondaryColor (#70A815) -> secondary actions, links, badges
 *  - surfaceColor (#DEE7E7) -> backgrounds, cards, surfaces
 *  - fontFamily (Roboto) -> font across the UI (weights: Regular, Medium, Semibold, Bold)
 */
export function generatePalette(config: ThemeColorsConfig) {
	const { primaryColor, secondaryColor, surfaceColor } = config;

	// Tertiary is a distinct role (JANUS lilac accent), NOT derived from
	// primary/secondary; falls back to the documented default (SIMS-7505 AC2).
	const tertiaryColor = config.tertiaryColor || DEFAULT_TERTIARY_COLOR;

	// surfaceTint is a distinct tinted-surface role (JANUS blue-100), NOT
	// surfaceLight/#FFFFFF or derived; falls back to the documented default (SIMS-7608 AC2).
	const surfaceTintColor = config.surfaceTint || DEFAULT_SURFACE_TINT;

	// SIMS-8458 — distinct design-system roles, each falling back to its documented
	// default (AC1/AC2/AC4). See the DEFAULT_* constants above for why none of these is
	// derived from an existing role.
	const successLight = config.successLight || DEFAULT_SUCCESS_LIGHT;
	const successDark = config.successDark || DEFAULT_SUCCESS_DARK;
	const brandFaintColor = config.brandFaint || DEFAULT_BRAND_FAINT;
	const dividerColor = config.dividerColor || DEFAULT_DIVIDER;
	const surfaceTwoColor = config.surfaceTwo || DEFAULT_SURFACE_TWO;
	const textPrimaryMain = config.textPrimaryMain || DEFAULT_TEXT_PRIMARY;
	const textSecondaryMain = config.textSecondaryMain || DEFAULT_TEXT_SECONDARY;

	const primaryDark = config.primaryDark || darken(primaryColor, 12);
	// primaryLight is used for inactive menu text/icons on the dark sidebar.
	// Derive a desaturated, high-lightness tint from the primary hue.
	// This matches the original purple behavior (#BAA3CA: h=275, s=27, l=72).
	const primaryHsl = rgbToHsl(hexToRgb(primaryColor));
	const primaryLight = rgbToHex(
		hslToRgb({ h: primaryHsl.h, s: Math.min(primaryHsl.s, 25), l: 72 }),
	);
	const primaryMain = primaryColor;

	const secondaryDark = darken(secondaryColor, 20);
	const secondaryLight = config.secondaryLight || lighten(secondaryColor, 30);

	// Surface. An ABSENT surfaceColor means the documented default — the same thing
	// SettingsModal means when it omits the key — not a value derived from the brand colour.
	const surfaceMain = surfaceColor || DEFAULT_SURFACE_COLOR;
	// The dark step still DERIVES from whatever surface is in force when the tenant set one,
	// since it is that surface's shadow; it falls back to the documented default only when
	// the tenant set no surface at all.
	const surfaceDark =
		config.surfaceDark ||
		(surfaceColor ? darken(surfaceColor, 8) : DEFAULT_SURFACE_DARK);
	const surfaceLight = config.surfaceLight || "#FFFFFF";

	// Outline colors derived from surface (overridable)
	const outlineMain =
		config.outlineMain || (surfaceColor ? darken(surfaceColor, 12) : "#D9D1DD");
	const outlineLight = surfaceColor ? lighten(surfaceColor, 5) : "#E4DEE9";
	const outlineDark =
		config.outlineDark || (surfaceColor ? darken(surfaceColor, 25) : "#ADA6B3");

	// Icon colors: neutral with primary accent
	const iconMain = surfaceColor ? darken(surfaceColor, 30) : "#ADA6B3";
	const iconLight = surfaceColor ? darken(surfaceColor, 15) : "#D9D1DD";

	return {
		mode: "light",
		primary: {
			main: primaryMain,
			light: primaryLight,
			dark: primaryDark,
		},
		secondary: {
			main: secondaryColor,
			light: secondaryLight,
			dark: secondaryDark,
		},
		tertiary: {
			main: tertiaryColor,
			light: lighten(tertiaryColor, 12),
			dark: darken(tertiaryColor, 12),
		},
		surfaceTint: {
			main: surfaceTintColor,
			light: lighten(surfaceTintColor, 4),
			dark: darken(surfaceTintColor, 6),
		},
		// SIMS-8458 AC1 — the design's success pair. A distinct role: never aliased to
		// outline.dark, which carries the clinical status colours (AC5).
		success: {
			main: successDark,
			light: successLight,
			dark: successDark,
		},
		// SIMS-8458 AC2 — brand-faint, for the selected day card. Not primary.light.
		brandFaint: {
			main: brandFaintColor,
			light: brandFaintColor,
			dark: brandFaintColor,
		},
		// SIMS-8458 AC11 — the design's --surface-2, a distinct role rather than a value
		// derived from the tenant's surfaceColor.
		surfaceTwo: {
			main: surfaceTwoColor,
			light: surfaceTwoColor,
			dark: surfaceTwoColor,
		},
		// SIMS-8458 AC4 — solid divider. Previously unset, so MUI's translucent default
		// composited differently per background.
		divider: dividerColor,
		// SIMS-8458 AC4 — MUI's text roles, where body and muted text actually resolve
		// (~173 call sites). Previously left at MUI defaults.
		text: {
			primary: textPrimaryMain,
			secondary: textSecondaryMain,
		},
		error: {
			main: "#AB0707",
			light: "#FCD6D0",
			dark: "#AB0707",
		},
		warning: {
			main: "#F8955B",
			light: DEFAULT_WARNING_LIGHT,
			dark: DEFAULT_WARNING_DARK,
		},
		// The readable text colours for a 12px pill on the matching `-light` fill. Additive:
		// no existing consumer reads these, so defining them cannot restyle another screen.
		successInk: {
			main: DEFAULT_SUCCESS_INK,
			light: DEFAULT_SUCCESS_INK,
			dark: DEFAULT_SUCCESS_INK,
		},
		warningInk: {
			main: DEFAULT_WARNING_INK,
			light: DEFAULT_WARNING_INK,
			dark: DEFAULT_WARNING_INK,
		},
		surface: {
			main: surfaceMain,
			light: surfaceLight,
			dark: surfaceDark,
			dark2: surfaceColor ? darken(surfaceColor, 30) : "#ADA6B3",
		},
		accent: {
			main: primaryDark,
		},
		icon: {
			main: iconMain,
			light: iconLight,
			dark: "#4C4C56",
			dark2: primaryDark,
		},
		outline: {
			main: outlineMain,
			light: outlineLight,
			dark: outlineDark,
			dark2: "#4C4C56",
		},
		menu: {
			main: primaryDark,
			light: primaryMain,
			light2: lighten(primaryMain, 8),
		},
		textMain: {
			main: "#E5E6E6",
			// SIMS-8458 AC4 — THEME_COLOR_MAP.md: "Texto secundario/subtítulo" (~51 sites),
			// so it resolves to the design's --text-secondary. Was #79797D.
			main2: config.textMainMain2 || DEFAULT_TEXT_SECONDARY,
			main3: primaryLight,
			// Matches the design's --text-primary-inv (#FFFFFF) already — no change.
			light: "#FFFFFF",
			// NOT changed by SIMS-8458: this is the nearest role to the design's
			// --text-disabled / --text-placeholder #B0B6B6, and at ~74 sites it is the
			// largest single text role in the app. Disabled text is neither body nor muted
			// text, so AC4's wording does not reach it. Its own ticket — see the
			// DEFAULT_TEXT_PRIMARY docblock.
			dark: "#CCCCCE",
			light2: "#FCFCFC",
			// SIMS-8458 AC4 — THEME_COLOR_MAP.md: "Texto principal de toda la app"
			// (~66 sites, the app's real body-text role), so it resolves to the design's
			// --text-primary. Was #1F2026.
			dark2: config.textMainDark2 || DEFAULT_TEXT_PRIMARY,
			light3: "#F8F8F8",
			dark3: "#F26716",
			light4: "#F5F5F5",
			dark4: secondaryDark,
		},
		// SIMS-8458 AC4 — body text. THEME_COLOR_MAP.md documents this as body text, so it
		// resolves to the design's --text-primary along with textMain.dark2 and MUI
		// text.primary. Was #1F2026 (one hex digit from the design's #1F2626 — the design's
		// :root block is authoritative, and the two are not the same colour).
		textPrimary: {
			main: textPrimaryMain,
			light: textPrimaryMain,
		},
		// SIMS-8458 AC4 — THEME_COLOR_MAP.md documents this as "Labels apagados, hints de
		// formularios", i.e. muted text, so it resolves to the design's --text-secondary.
		textSecondary: {
			main: textSecondaryMain,
			light: primaryLight,
		},
		textLinkHelpSupport: {
			main: darken(secondaryColor, 10),
		},
	};
}
